// RefactoredItemModal.jsx
import { useEffect, useState } from "react";
import BottomSheet from "../components/BottomSheet";
import { getItemOptions } from "../api/apis";
import { mapIngredientsBySettings } from "../components/IngredientGroupList";

export default function RefactoredItemModal({ item, setItem, add, settings }) {
  const [selected, setSelected] = useState({
    removals: [],
    productAddOns: [],
    menuAddOns: [],
    extras: [],
    sides: [],
    steps: {},
  });
  const [specialRequest, setSpecialRequest] = useState("");
  const [addons, setAddons] = useState({ T: [], S: [] });
  const [totalPrice, setTotalPrice] = useState(item?.price / 100 || 0);

  useEffect(() => {
    if (item) {
      setSelected({ removals: [], productAddOns: [], menuAddOns: [], extras: [], sides: [], steps: {} });
      setSpecialRequest("");
      setTotalPrice(item.price / 100);
      getItemOptions(item.mnuid)
        .then(setAddons)
        .catch(console.error);
    } else {
      setAddons({ T: [], S: [] });
    }
  }, [item]);

  if (!item) return null;

  const mapped = mapIngredientsBySettings(item.pbo.iga || [], settings);

  // Determine if any obligatory step group is still unselected
  const hasMissingMandatory = Object.entries(mapped.steps).some(([groupKey, options]) => {
    const header = options.find(o => o._meta.idx === "0");
    return header._meta.typeDef.obligatory && !selected.steps[groupKey];
  });

  const updateSelection = (key, payload, single = false) => {
    setSelected(prev => {
      const current = prev[key];
      if (single) {
        const { groupKey, option } = payload;
        return { ...prev, steps: { ...prev.steps, [groupKey]: option } };
      } else {
        const { id, price } = payload;
        const exists = current.find(i => i.id === id);
        const nextList = exists ? current.filter(i => i.id !== id) : [...current, payload];
        setTotalPrice(tp => tp + (exists ? -price : price));
        return { ...prev, [key]: nextList };
      }
    });
  };

  const handleAddToCart = () => {
    const customized = {
      ...item,
      customizations: {
        removed: selected.removals,
        special: selected.productAddOns,
        addons: selected.menuAddOns,
        sides: selected.sides,
        steps: selected.steps,
        comment: specialRequest,
      },
      finalPrice: Math.round(totalPrice * 100),
    };
    add(customized);
    setItem(null);
  };

  return (
    <BottomSheet
      headerText={item.text.toSwe()}
      saveAction={handleAddToCart}
      showModal={!!item}
      setShowModal={() => setItem(null)}
      saveText="Lägg till i beställning"
      disabled={hasMissingMandatory}
    >
      <div className="overflow-y-scroll" style={{ maxHeight: "70dvh" }}>
        <p className="text-muted mb-4">{item.desc.toSwe()}</p>

        {/* Step choice groups */}
        {Object.entries(mapped.steps).map(([groupKey, options]) => {
          const header = options.find(o => o._meta.idx === "0");
          const choices = options.filter(o => o._meta.idx !== "0");
          return (
            <OptionSection
              key={groupKey}
              title={header.p0text}
              items={choices.map(o => ({ id: o.p0lopnr, label: o.p0text, price: 0 }))}
              type="radio"
              selected={selected.steps[groupKey]}
              onChange={opt => updateSelection('steps', { groupKey, option: opt }, true)}
              required={header._meta.typeDef.obligatory}
            />
          );
        })}

        {/* Removals */}
        {mapped.removables.length > 0 && (
          <OptionSection
            title="Ta bort"
            items={mapped.removables.map(i => ({ id: i.p0lopnr, label: i.p0text, price: 0 }))}
            type="checkbox"
            selected={selected.removals}
            onChange={opt => updateSelection('removals', { id: opt.id, label: opt.label, price: 0 })}
          />
        )}

        {/* Product-specific add-ons */}
        {mapped.addOns.length > 0 && (
          <OptionSection
            title="Specialbeställning"
            items={mapped.addOns.map(o => ({ id: o.p0lopnr, label: o.p0text, price: o.p0pris / 100 }))}
            type="checkbox"
            selected={selected.productAddOns}
            onChange={opt => updateSelection('productAddOns', opt)}
            isopen={true}
          />
        )}

        {/* Combined menu add‑ons and extras under “Lägg till” */}
        {(mapped.extras.length > 0 || addons.T?.length > 0) && (
          <OptionSection
            title="Lägg till"
            items={
              (addons.T || [])
              .filter(item=>(item.m0sort < 600 || mapped.extras.includes(item.m0katnr)))
              .map(a => ({ id: a.m0katnr, label: a.m0text.toSwe(), price: a.m0pris / 100 }))}
        
            type="checkbox"
            selected={selected.menuAddOns}
            onChange={opt => updateSelection('menuAddOns', opt)}
          />
        )}

        {/* Sides */}
        {addons.S?.length > 0 && (
          <OptionSection
            title="Sides"
            items={addons.S.filter(item=>item.m0sort < 900).map(a => ({ id: a.m0katnr, label: a.m0text.toSwe(), price: a.m0pris / 100 }))}
            type="checkbox"
            selected={selected.sides}
            onChange={opt => updateSelection('sides', opt)}
          />
        )}

        {/* Allergens */}
        {mapped.infoOnly.length > 0 && (
          <div className="mb-3">
            <h6>Allergener:</h6>
            <ul className="list-unstyled mb-0">
              {mapped.infoOnly.map(i => (
                <li key={i.p0lopnr}>{i.p0text}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Special request */}
        <div className="mb-4">
          <h6 className="mb-2">Övrig information</h6>
          <textarea
            className="form-control bg-light"
            rows={2}
            value={specialRequest}
            onChange={e => setSpecialRequest(e.target.value)}
          />
        </div>
      </div>

      {/* Total */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <h6 className="m-0">Totalbelopp:</h6>
        <h5 className="fw-bold">{totalPrice.toFixed(2)} kr</h5>
      </div>
    </BottomSheet>
  );
}



function OptionSection({
  title,
  items = [],
  type = 'checkbox',
  selected,
  onChange,
  required = false,
  isopen = false
}) {
  const [open, setOpen] = useState(required || isopen);

  return (
    <div className="mb-3 border rounded shadow-sm">
      <div
        className={`d-flex justify-content-between align-items-center ${required ? 'bg-warning-subtle' : 'bg-light'} px-3 py-2 cursor-pointer`}
        onClick={() => setOpen(prev => !prev)}
      >
        <h6 className="mb-0">
          {title} {required && '*'}
        </h6>
        <span className="text-secondary fs-4" style={{ userSelect: 'none' }}>
          {open ? '−' : '+'}
        </span>
      </div>

      {open && (
        <div className="p-3">
          {items.map(item => {
            const isChecked =
              type === 'checkbox'
                ? selected.some(i => i.id === item.id)
                : selected?.id === item.id;

            return (
              <div key={item.id} className="form-check mb-2">
                <input
                  className="form-check-input"
                  type={type}
                  id={`${title}-${item.id}`}
                  name={type === 'radio' ? title : undefined}
                  checked={isChecked}
                  onChange={() => onChange(item)}
                />
                <label className="form-check-label" htmlFor={`${title}-${item.id}`}> 
                  {item.label}
                  {item.price > 0 && (
                    <span className="text-muted"> (+{item.price.toFixed(2)} kr)</span>
                  )}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
